import { RISK_LEVEL_OPTIONS } from "./business";

export const BPJS_CEILING = 12000000;
export const BPJS_HEALTH_EMPLOYER_PERCENTAGE = 0.04; // 4%
export const BPJS_HEALTH_EMPLOYEE_PERCENTAGE = 0.01; // 1%

// The lowest risk class, and the rate for an employee whose risk level was
// never recorded.
export const BPJS_JKK_PERCENTAGE = 0.0024; // 0.24%
export const BPJS_JKM_PERCENTAGE = 0.003; // 0.30%
const BPJS_JHT_PERCENTAGE = 0.02; // 2%
const BPJS_JHT_EMPLOYER_PERCENTAGE = 0.037; // 3.7%
// The pension (JP) wage cap moves on a fixed date, so a single number cannot
// express it: a payroll row for January 2026 is still owed the figure that was
// in force then, not the one that replaced it in March. Ordered newest first;
// the last entry has no `from` and answers for every period before the one
// above it, and `from` is a plain YYYY-MM-DD calendar date, not an instant.
const BPJS_JP_MAX_SCHEDULE = [
  // Wage cap Rp11,086,300, employee share 1%.
  { from: "2026-03-01", amount: 110863 },
  // Wage cap Rp10,547,400, employee share 1%.
  { from: null, amount: 105474 },
];
const BPJS_JP_PERCENTAGE = 0.01; // 1%
const BPJS_JP_EMPLOYER_PERCENTAGE = 0.02; // 2%

export const CONTRIBUTION_CONFIGS = {
  BPJS_KESEHATAN: {
    percentage: BPJS_HEALTH_EMPLOYEE_PERCENTAGE,
    usesCeiling: true,
  },
  BPJS_KETENAGAKERJAAN_JHT: {
    percentage: BPJS_JHT_PERCENTAGE,
    employerPercentage: BPJS_JHT_EMPLOYER_PERCENTAGE,
    usesCeiling: false,
  },
  BPJS_KETENAGAKERJAAN_JP: {
    percentage: BPJS_JP_PERCENTAGE,
    employerPercentage: BPJS_JP_EMPLOYER_PERCENTAGE,
    usesCeiling: true,
    maxAmountSchedule: BPJS_JP_MAX_SCHEDULE,
  },
  BPJS_KETENAGAKERJAAN_JKK: {
    percentage: BPJS_JKK_PERCENTAGE,
    usesCeiling: false,
    usesRiskLevel: true,
    formatFixed: true,
  },
  BPJS_KETENAGAKERJAAN_JKM: {
    percentage: BPJS_JKM_PERCENTAGE,
    usesCeiling: false,
    formatFixed: true,
  },
};

// The JKK rate follows the risk class of the work (PP 44/2015), recorded on the
// employee as `riskLevel`. Accepts the stored value or the option object a form
// holds; an empty or unknown level is the lowest class.
export const getJKKRate = (riskLevel) => {
  const value = riskLevel?.value || riskLevel;
  const option = RISK_LEVEL_OPTIONS.find((item) => item.value === value);
  return option ? option.rate : BPJS_JKK_PERCENTAGE;
};

// The scheduled figure in force on `date`. Accepts a Date, a dayjs, a
// timestamp or a date string; an unusable one is read as now rather than as
// the beginning of time, so a bad argument cannot quietly resurrect a rate
// that expired years ago.
export const getScheduledAmount = (schedule, date = new Date()) => {
  if (!schedule?.length) return null;

  // `null` has to be caught before Date sees it: `new Date(null)` is the 1970
  // epoch, which would hand back the oldest rate in the table.
  const raw = date == null ? new Date() : (date.valueOf?.() ?? date);
  const parsed = new Date(raw);
  const at = Number.isNaN(parsed.valueOf()) ? new Date() : parsed;

  // Compared as calendar dates, never as instants. `new Date("2026-03-01")` is
  // UTC midnight, which is still February anywhere east of Greenwich, so a
  // payroll row for local 1 March would be handed the rate that expired on it.
  const on = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(at.getDate()).padStart(2, "0")}`;

  const entry = schedule.find((step) => !step.from || step.from <= on);

  return entry ? entry.amount : null;
};

// The contribution ceiling for `config` on `date`, or null where the
// contribution has none. Every caller has to say which period it means: the
// answer for a payroll month is not the answer for today.
export const getContributionMaxAmount = (config, date) =>
  config?.maxAmountSchedule
    ? getScheduledAmount(config.maxAmountSchedule, date)
    : null;
